// Deep link handling and parsing for the Web SDK.
// Parses URL parameters for deep link data (scheme, host, path, query params)
// and extracts attribution parameters (fbclid, gclid, ttclid, etc.).

export interface DeepLinkData {
  url: string;
  scheme: string;
  host: string;
  path: string;
  queryParams: Record<string, string>;
  timestamp: number;
}

/**
 * Parse a URL string into structured DeepLinkData.
 * Returns null if the URL cannot be parsed.
 */
export function parseDeepLink(url: string): DeepLinkData | null {
  try {
    const parsed = new URL(url);
    const params: Record<string, string> = {};
    parsed.searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return {
      url,
      scheme: parsed.protocol.replace(':', ''),
      host: parsed.hostname,
      path: parsed.pathname,
      queryParams: params,
      timestamp: Date.now()
    };
  } catch {
    return null;
  }
}

/**
 * Set up a listener for URL changes in a web browser context.
 * Listens for `popstate` and `hashchange` events and calls the callback
 * with parsed deep link data on each URL change.
 *
 * Returns an unsubscribe function to remove the listeners.
 */
export function setupDeepLinkListener(onDeepLink: (data: DeepLinkData) => void): () => void {
  if (typeof window === 'undefined' || !window.location) return () => {};

  let lastUrl: string;
  try {
    lastUrl = window.location.href;
  } catch {
    return () => {};
  }

  const handleUrlChange = (): void => {
    const currentUrl = window.location.href;
    if (currentUrl === lastUrl) return;
    lastUrl = currentUrl;

    const parsed = parseDeepLink(currentUrl);
    if (parsed) onDeepLink(parsed);
  };

  window.addEventListener('popstate', handleUrlChange);
  window.addEventListener('hashchange', handleUrlChange);

  // Also check the initial URL
  const initialData = parseDeepLink(window.location.href);
  if (initialData && Object.keys(initialData.queryParams).length > 0) {
    onDeepLink(initialData);
  }

  return () => {
    window.removeEventListener('popstate', handleUrlChange);
    window.removeEventListener('hashchange', handleUrlChange);
  };
}
